import { useCallback } from 'react';
import { AIAgentId } from '../enum';

/**
 * Navigation agent result structure from the backend
 */
export interface NavigationAgentResult {
  id: number;
  title: string;
  sid: string;
  positionX: number | null;
  positionY: number | null;
  positionZ: number | null;
  type: string;
}

export interface NavigationAgentResponse {
  agentId: string;
  results?: NavigationAgentResult[];
  userFriendlyExplanation?: string;
  summary?: string;
  [key: string]: any;
}

interface UseNavigationAgentParams {
  showcase?: any;
  tags?: any[];
}

export function useNavigationAgent({ showcase, tags }: UseNavigationAgentParams) {

  const hasValidPosition = useCallback((result: NavigationAgentResult): boolean => {
    return (
      result.positionX !== null &&
      result.positionY !== null &&
      result.positionZ !== null &&
      !isNaN(result.positionX) &&
      !isNaN(result.positionY) &&
      !isNaN(result.positionZ)
    );
  }, []);


  const navigateToPosition = useCallback(async (
    result: NavigationAgentResult
  ): Promise<boolean> => {
    if (!showcase?.Mattertag) {
      console.error('[NavigationAgent] Showcase Mattertag not available');
      return false;
    }

    try {
      console.log('[NavigationAgent] Navigating to tag:', {
        title: result.title,
        sid: result.sid,
      });

      await showcase.Mattertag.navigateToTag(
        result.sid,
        showcase.Mattertag.Transition.FLY
      );

      console.log('[NavigationAgent] Navigation successful');
      return true;
    } catch (error) {
      console.error('[NavigationAgent] Failed to navigate to tag:', error);
      return false;
    }
  }, [showcase]);

  const handleNavigationAgentResponse = useCallback(async (
    data: NavigationAgentResponse
  ): Promise<string> => {
    if (data.agentId !== AIAgentId.NavigationAgent) {
      console.warn('[NavigationAgent] Called with non-navigation-agent response:', data.agentId);
      return '';
    }

    console.log('[NavigationAgent] Processing navigation-agent response:', data);

    // Check if we have results with positions
    const results = data.results || [];

    if (results.length === 0) {
      console.log('[NavigationAgent] No results found in response');
      return "I cannot locate that place. Please try to specify your request.";
    }

    // Find the first result with valid position
    const validResult = results.find(hasValidPosition);

    if (!validResult) {
      console.log('[NavigationAgent] No valid position found in results');
      return "I cannot locate that place. Please try to specify your request.";
    }

    const matchingTag = tags?.find((tag: any) => tag.id === validResult.id);

    if (!matchingTag) {
      console.error('[NavigationAgent] Tag not found in tags list:', validResult.id);
      return `Found "${validResult.title}" but it's not available in the current tour.`;
    }

    console.log('[NavigationAgent] Found matching tag:', {
      responseId: validResult.id,
      tagId: matchingTag.id,
      tagSid: matchingTag.sid,
      title: matchingTag.title || validResult.title,
    });

    const tagToNavigate = {
      ...validResult,
      sid: matchingTag.sid,
      title: matchingTag.title || validResult.title,
    };

    // Navigate to the position
    const navigationSuccess = await navigateToPosition(tagToNavigate);

    if (navigationSuccess) {
      return `Navigating to "${tagToNavigate.title}"...`;
    } else {
      return `Found "${tagToNavigate.title}" but failed to navigate. Please try again.`;
    }
  }, [hasValidPosition, navigateToPosition, tags]);

  return {
    handleNavigationAgentResponse,
    hasValidPosition,
    navigateToPosition,
  };
}

